/**
 * Iter 231 · Wave 4 · Tranche 2 — Gov AirQ (DGCA) portal.
 * Lives inside the existing /gov layout, gov-user auth.
 * 4 sub-tabs: Clearances · Registry · Pad compliance · Incidents.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { AirplaneTilt, ShieldCheck, Warning, Files, Plus } from "@phosphor-icons/react";

const TABS = [
  { key: "clearances",   label: "Clearances",     icon: ShieldCheck },
  { key: "registry",     label: "Registry",       icon: AirplaneTilt },
  { key: "pads",         label: "Pad compliance", icon: Files },
  { key: "incidents",    label: "Incidents",      icon: Warning },
];

export default function GovAirq() {
  const [tab, setTab] = useState("clearances");
  return (
    <div className="p-6 space-y-4" data-testid="gov-airq-root">
      <div>
        <h1 className="font-heading text-2xl">AirQ · DGCA / Aviation Authority</h1>
        <p className="text-sm text-[#5C5A55] mt-1">
          Read-mostly regulator surface over Rescigo's aeromedical fleet.
        </p>
      </div>
      <div className="flex items-center gap-1 border-b border-[#E5E2D9] overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`gov-airq-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                active ? "border-[#0F766E] text-[#0F766E]" : "border-transparent text-[#5C5A55]"
              }`}>
              <Icon size={14} weight={active?"fill":"regular"}/>{t.label}
            </button>
          );
        })}
      </div>
      {tab === "clearances" && <Clearances/>}
      {tab === "registry"   && <Registry/>}
      {tab === "pads"       && <PadCompliance/>}
      {tab === "incidents"  && <Incidents/>}
    </div>
  );
}

function Clearances() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = async () => {
    try {
      const q = status ? `?status=${status}` : "";
      const r = await api.get(`/gov/airq/clearances${q}&days=30`.replace("?&","?"));
      setRows(r.data.clearances || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status]);   // eslint-disable-line
  const act = async (mid, action) => {
    const ref = window.prompt(`${action} clearance ref (optional):`) || null;
    try {
      await api.post(`/gov/airq/clearances/${mid}/action`, { action, reference: ref });
      toast.success(`Clearance ${action}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="gov-airq-clearances">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#5C5A55]">{rows.length} missions in window</div>
        <select value={status} onChange={e => setStatus(e.target.value)}
                  data-testid="clr-filter"
                  className="border rounded px-2 py-1 text-sm bg-white">
          <option value="">All statuses</option>
          <option value="cleared">Cleared</option>
          <option value="denied">Denied</option>
          <option value="info_requested">Info requested</option>
        </select>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No missions</div>
          : rows.slice(0,50).map(m => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`clr-row-${m.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{m.mission_no} · {m.mission_kind}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {m.aircraft_call_sign || "unassigned"} · P{m.priority?.replace("P","")} · {m.distance_km}km
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Pill status={m.clearance_status || "pending"}/>
                {!m.clearance_status && (
                  <>
                    <button onClick={() => act(m.id, "approve")}
                              data-testid={`clr-approve-${m.id.slice(0,6)}`}
                              className="text-[11px] px-2 py-1 rounded bg-[#166534] text-white">
                      Clear
                    </button>
                    <button onClick={() => act(m.id, "reject")}
                              className="text-[11px] px-2 py-1 rounded bg-[#7F1D1D] text-white">
                      Deny
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Registry() {
  const [ac, setAc] = useState([]);
  const [crew, setCrew] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [a, c] = await Promise.all([
          api.get("/gov/airq/registry/aircraft"),
          api.get("/gov/airq/registry/crew"),
        ]);
        setAc(a.data.aircraft || []); setCrew(c.data.crew || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="gov-airq-registry">
      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
          Aircraft registry · {ac.length}
        </div>
        {ac.length === 0 ? <div className="text-sm text-[#8A8780]">No aircraft</div>
          : ac.map(a => (
            <div key={a.id} className="py-1.5 border-b last:border-0 text-sm">
              <div className="font-mono font-medium">{a.call_sign}</div>
              <div className="text-[11px] text-[#8A8780]">
                {a.kind} · {a.manufacturer} {a.model} · {a.ambulance_type || "n/a"} · {a.dgca_reg_no || "no DGCA reg"}
              </div>
            </div>
          ))}
      </div>
      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
          Crew registry · {crew.length}
        </div>
        {crew.length === 0 ? <div className="text-sm text-[#8A8780]">No aviation crew</div>
          : crew.map(c => (
            <div key={c.id} className="py-1.5 border-b last:border-0 text-sm">
              <div className="font-medium">{c.name}</div>
              <div className="text-[11px] text-[#8A8780]">{c.role} · {c.phone}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

function PadCompliance() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/gov/airq/pad-compliance")
      .then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="gov-airq-pads">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Pads" v={data.count} c="#0369A1"/>
        <Stat label="Pending approvals" v={data.pending_approvals} c="#B45309"/>
        <Stat label="Expiring ≤ 30d" v={data.expiring_docs_30d} c="#B91C1C"/>
      </div>
      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Pads</div>
        {data.pads.map(p => (
          <div key={p.id} className="py-1.5 border-b last:border-0 text-sm">
            <div className="font-medium">{p.name}</div>
            <div className="text-[11px] text-[#8A8780]">
              {p.kind} · {p.district || "?"} · {p.ownership || "?"} · lic {p.licence_no || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Incidents() {
  const [rows, setRows] = useState([]);
  const [severity, setSeverity] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try {
      const q = severity ? `?severity=${severity}` : "";
      const r = await api.get(`/gov/airq/incidents${q}`);
      setRows(r.data.incidents || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [severity]);   // eslint-disable-line
  const close = async (iid) => {
    try {
      await api.post(`/gov/airq/incidents/${iid}/close?note=closed by regulator`);
      toast.success("Closed"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="gov-airq-incidents">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#5C5A55]">{rows.length} incidents (90d)</div>
        <div className="flex items-center gap-2">
          <select value={severity} onChange={e => setSeverity(e.target.value)}
                    data-testid="inc-filter"
                    className="border rounded px-2 py-1 text-sm bg-white">
            <option value="">All severities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <button onClick={() => setShowAdd(true)} data-testid="inc-add"
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#B91C1C] text-white rounded">
            <Plus size={12}/>Report
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No incidents</div>
          : rows.map(i => (
            <div key={i.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`inc-row-${i.id.slice(0,6)}`}>
              <div>
                <div className="font-medium">{i.incident_no} · {i.summary}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {i.severity} · {(i.at||"").slice(0,16)} · by {i.reported_by || "unknown"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Pill status={i.status}/>
                {i.status === "open" && (
                  <button onClick={() => close(i.id)}
                            data-testid={`inc-close-${i.id.slice(0,6)}`}
                            className="text-[11px] px-2 py-1 rounded bg-[#059669] text-white">
                    Close
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>
      {showAdd && <IncidentModal onClose={() => setShowAdd(false)}
                                          onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function IncidentModal({ onClose, onSaved }) {
  const [f, setF] = useState({ severity: "medium", summary: "",
                                     reported_by: "", mission_id: "", aircraft_id: "" });
  const submit = async () => {
    try {
      await api.post("/gov/airq/incidents", f);
      toast.success("Incident logged"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <h2 className="font-heading text-lg mb-4">Report incident</h2>
        <select value={f.severity} onChange={e => setF({...f, severity:e.target.value})}
                  data-testid="inc-sev"
                  className="w-full border rounded px-3 py-2 mb-2 text-sm bg-white">
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <input placeholder="Summary" value={f.summary} onChange={e => setF({...f, summary:e.target.value})}
                 data-testid="inc-sum"
                 className="w-full border rounded px-3 py-2 mb-2 text-sm"/>
        <input placeholder="Reported by" value={f.reported_by} onChange={e => setF({...f, reported_by:e.target.value})}
                 data-testid="inc-rep"
                 className="w-full border rounded px-3 py-2 mb-2 text-sm"/>
        <button onClick={submit} data-testid="inc-save"
                  className="w-full py-2 rounded bg-[#B91C1C] text-white">Log incident</button>
      </div>
    </div>
  );
}


function Stat({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[22px] font-heading font-semibold" style={{color:c}}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function Pill({ status }) {
  const palette = {
    cleared: "#166534", denied: "#7F1D1D", info_requested: "#B45309",
    pending: "#4338CA", open: "#B91C1C", closed: "#166534",
  };
  return (
    <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium"
            style={{color: palette[status] || "#5C5A55",
                      backgroundColor: (palette[status] || "#8A8780")+"22"}}>
      {status}
    </span>
  );
}
